import type { ProfileFamily } from '@modules/professional/util/derive-profile-family';

/**
 * URL param carrying the Products tab's selected brands as a comma-separated
 * list of profile URL segments. Its own key rather than the shared `company`
 * filter: that one is overwritten by the tab's predefined professional scope,
 * and `brands` already means product brands in the search filters.
 */
export const FAMILY_BRANDS_PARAM = 'family';

/** Read the selected brand segments off a URL, in the order given. */
export const readFamilyBrandParam = (params: URLSearchParams): string[] =>
    (params.get(FAMILY_BRANDS_PARAM) ?? '')
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean);

interface FamilyBrandScope {
    /** Every brand whose products this tab may show. */
    selectable: string[];
    /** The scope with no explicit choice — the profile's own products. */
    fallback: string[];
}

/**
 * Resolve the Products tab's professional scope from the URL.
 *
 * With no `family` param the tab shows what it always has: the profile's own
 * products, plus any network owner that has explicitly shared its products with
 * it (the BM's `productSharing`), so existing networks don't change. Ticking a
 * sister brand widens the scope to exactly the ticked brands. Anything not in
 * the family is dropped, so a crafted URL can't pull an unrelated
 * professional's products onto this profile.
 */
export const resolveFamilyBrandScope = (
    params: URLSearchParams,
    { selectable, fallback }: FamilyBrandScope,
): string[] => {
    const allowed = new Set(selectable);
    const requested = readFamilyBrandParam(params).filter((segment) =>
        allowed.has(segment),
    );
    return requested.length > 0 ? requested : fallback;
};

/** Every brand the chips may offer: this profile, then the rest of the family. */
export const familyBrandSegments = (
    urlSegment: string,
    family: ProfileFamily | null,
    shared: string[] = [],
): string[] => [
    ...new Set([
        urlSegment,
        ...shared,
        ...(family?.others.map((brand) => brand.segment) ?? []),
    ]),
];
